import { useState, useEffect, useCallback } from 'react';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Search, LogOut, User, Radio, Plus, Edit, ArrowLeft } from 'lucide-react';
import { Channel, Station } from '../data/mockData';
import { CreateChannelDrawer } from './CreateChannelDrawer';
import { EditChannelDrawer } from './EditChannelDrawer';
import { NotificationBell } from './NotificationBell';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useListChannelsPaginatedQuery } from '../../store/api';

interface ChannelListScreenProps {
  station: Station;
  userRole: 'station' | 'superadmin';
  currentUsername: string;
  onSelectChannel: (channel: Channel) => void;
  onCreateChannel: (channel: Omit<Channel, 'id' | 'stationId'>) => void;
  onUpdateChannel: (channelId: string | number, channel: Omit<Channel, 'id' | 'stationId'>) => void;
  onDeleteChannel: (channelId: string | number) => void;
  onLogout: () => void;
  onBackToStations?: () => void;
}

export function ChannelListScreen({
  station,
  userRole,
  currentUsername,
  onSelectChannel,
  onCreateChannel,
  onUpdateChannel,
  onDeleteChannel,
  onLogout,
  onBackToStations,
}: ChannelListScreenProps) {
  const formatChannelName = (name: string) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
  const displayName = currentUsername || 'User';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Server-side paginated channels
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 20;

  const { data: channelsData, isFetching } = useListChannelsPaginatedQuery({
    stationId: Number(station.id),
    offset: pageOffset,
    limit: PAGE_LIMIT,
    search: debouncedSearch || undefined,
  });

  const channels: Channel[] = (channelsData?.items ?? []).map((c: any) => ({
    ...c,
    id: String(c.id),
    stationId: String(c.stationId ?? c.station_id),
    channelNumber: c.channelNumber ?? c.channel_number,
    adminSuspend: Boolean(c.adminSuspend ?? c.admin_suspend),
    stationSuspend: Boolean(c.stationSuspend ?? c.station_suspend),
  }));

  const loadMore = useCallback(() => {
    if (channelsData?.hasMore && !isFetching) {
      setPageOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [channelsData?.hasMore, isFetching]);

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore: channelsData?.hasMore ?? false,
    isFetching,
    onLoadMore: loadMore,
  });

  // Reset pagination on search change
  useEffect(() => {
    setPageOffset(0);
  }, [debouncedSearch]);

  const handleEdit = (channel: Channel) => {
    setSelectedChannel(channel);
    setIsEditDrawerOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {userRole === 'superadmin' && onBackToStations && (
              <>
                <Button
                  variant="ghost"
                  onClick={onBackToStations}
                  className="gap-2 text-gray-300 hover:text-white hover:bg-gray-800"
                >
                  <ArrowLeft className="size-4" />
                  Back
                </Button>
                <div className="h-6 w-px bg-gray-700" />
              </>
            )}
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    className="text-gray-400 hover:text-white cursor-pointer"
                    onClick={onBackToStations}
                  >
                    {station.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-gray-500" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-white font-bold">
                    Channels
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell stationId={station.id as number} variant="dark" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-gray-300 hover:text-white hover:bg-gray-800">
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-blue-600 text-white">
                      <User className="size-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800">
                <DropdownMenuItem onClick={onLogout} className="text-gray-300 cursor-pointer hover:bg-gray-800 focus:bg-gray-800">
                  <LogOut className="mr-2 size-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Channels</h1>
              <Badge className="bg-blue-900 text-blue-200 hover:bg-blue-900">
                {station.name}
              </Badge>
            </div>
            <p className="text-gray-400">
              Manage channels and their content
            </p>
          </div>
          <Button
            onClick={() => setIsCreateDrawerOpen(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            <Plus className="size-4" />
            Create Channel
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
            <Input
              placeholder="Search by name or ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Channels Table */}
        {channels.length === 0 && !isFetching ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
            <p className="text-gray-400 text-lg">No channels found.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400 w-24">Serial No</TableHead>
                  <TableHead className="text-gray-400">Channel Name</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {channels.map((channel, index) => {
                  const isSuspended = channel.status === 'Suspended';
                  const isAdminSuspendedForStation =
                    userRole === 'station' && Boolean(channel.adminSuspend);

                  return (
                    <TableRow
                      key={channel.id}
                      className={`border-gray-800 ${
                        isSuspended ? 'opacity-50' : 'hover:bg-gray-800'
                      }`}
                    >
                      <TableCell className="text-gray-400 font-mono">
                        {index + 1}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {formatChannelName(channel.name)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={channel.status === 'Active' ? 'default' : 'secondary'}
                          className={
                            channel.status === 'Active'
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                          }
                        >
                          {channel.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(channel)}
                            disabled={isAdminSuspendedForStation}
                            className="gap-1 text-gray-200 hover:text-white hover:bg-gray-800"
                            title={
                              isAdminSuspendedForStation
                                ? 'Channel is suspended by admin. Only admin can edit.'
                                : undefined
                            }
                          >
                            <Edit className="size-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            disabled={isSuspended}
                            onClick={() => onSelectChannel(channel)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Open
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {(channelsData?.hasMore ?? false) && (
          <div ref={sentinelRef} className="flex justify-center py-6">
            <div className="text-sm text-gray-500">Loading more...</div>
          </div>
        )}
      </main>

      {/* Drawers */}
      <CreateChannelDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        onCreate={(data) => {
          onCreateChannel(data);
          setIsCreateDrawerOpen(false);
        }}
        existingChannelNumbers={channels.map(c => c.channelNumber)}
      />

      {selectedChannel && (
        <EditChannelDrawer
          isOpen={isEditDrawerOpen}
          onClose={() => {
            setIsEditDrawerOpen(false);
            setSelectedChannel(null);
          }}
          onSave={(data) => {
            onUpdateChannel(selectedChannel.id, data);
            setIsEditDrawerOpen(false);
            setSelectedChannel(null);
          }}
          onDelete={() => {
            setShowDeleteConfirm(true);
          }}
          channel={selectedChannel}
          existingChannelNumbers={channels.filter(c => c.id !== selectedChannel.id).map(c => c.channelNumber)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Delete channel "{selectedChannel?.name}"? This will soft-delete its programs and tracks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedChannel) {
                  onDeleteChannel(selectedChannel.id);
                  setIsEditDrawerOpen(false);
                  setSelectedChannel(null);
                }
                setShowDeleteConfirm(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}